import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { columnOf, tableTechnical } from '@/frontend/features/Fde/dataHealth/measures'
import type { HealthCopy, HealthTable } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatScalar } from '@/frontend/features/Fde/dataHealth/types'

// Names from the connector dictionary: the friendly label first, the technical name after it in small gray
// text. Without a dictionary, the technical name alone. Hover shows the dictionary text, never a guess.

function WithTip({ lines, className, children }: { lines: string[]; className?: string; children: React.ReactNode }) {
  if (!lines.length) return <span className={className}>{children}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('cursor-help decoration-muted-foreground/50 decoration-dotted underline-offset-2 hover:underline', className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={4} className="grid max-w-72 gap-0.5 text-left">
        {lines.map((line, index) => <span key={index}>{line}</span>)}
      </TooltipContent>
    </Tooltip>
  )
}

export function TableName({ id, table, copy, className }: { id: string; table?: HealthTable; copy: HealthCopy; className?: string }) {
  const label = table?.label?.trim()
  const technical = tableTechnical(id, table)
  const grain = table?.grain?.trim()
  return (
    <WithTip lines={grain ? [format(copy.table_grain, { grain })] : []} className={cn('break-words', className)}>
      {label
        ? <><span className="font-medium">{label}</span> <span className="break-all font-mono text-[11px] font-normal text-muted-foreground">{technical}</span></>
        : <span className="break-all font-mono text-xs">{technical}</span>}
    </WithTip>
  )
}

export function ColumnName({ table, name, copy, className }: { table?: HealthTable; name: string; copy: HealthCopy; className?: string }) {
  const column = columnOf(table, name)
  const label = column?.label?.trim()
  const lines = [
    column?.description?.trim() || null,
    column?.unit ? format(copy.column_unit, { unit: column.unit }) : null,
    column?.example != null && column.example !== '' ? format(copy.column_example, { example: formatScalar(column.example) }) : null,
  ].filter((line): line is string => !!line)
  return (
    <WithTip lines={lines} className={cn('break-words', className)}>
      {label && label !== name
        ? <>{label} <span className="break-all font-mono text-[11px] text-muted-foreground">({name})</span></>
        : <span className="break-all font-mono text-xs">{name}</span>}
    </WithTip>
  )
}
